import L from "leaflet";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import {
  Circle,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
} from "react-leaflet";

import "leaflet/dist/leaflet.css";
import { MapAddress } from "./map-location.interface";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export interface MarkerData {
  id: string;
  type: string;
  lat: number;
  long: number;
}

interface MapProps {
  open?: boolean;
  propMarkers?: MarkerData[];
  projectAddress?: MapAddress;
  onClose?: () => void;
}

const colorMap: Record<string, string> = {
  CLOCK_IN: "#D81B60",
  CLOCK_OUT: "#424242",
  BREAK_START: "#0D47A1",
  center: "#000000",
  BREAK_END: "#FF8C00",
};

const LocationMap = ({
  open,
  propMarkers = [],
  projectAddress,
  onClose,
}: MapProps): JSX.Element => {
  const initialPosition: [number, number] = projectAddress
    ? [
        parseFloat(projectAddress.latitude),
        parseFloat(projectAddress.longitude),
      ]
    : [51.505, -0.09];
  const [markers, setMarkers] = useState<MarkerData[]>([...propMarkers]);
  const [isContinueButtonOn] = useState(false);

  const generateId = (): string =>
    `marker-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const addMarker = (type: string): void => {
    return;

    if (type === "break" || !markers.some((marker) => marker.type === type)) {
      setMarkers((prev) => [
        ...prev,
        {
          id: generateId(),
          type,
          lat: initialPosition[0],
          long: initialPosition[1],
        },
      ]);
    }
  };
  const returnTypeName = (type: string): string => {
    switch (type) {
      case "CLOCK_IN":
        return "Clock In";
      case "CLOCK_OUT":
        return "Clock Out";
      case "BREAK_START":
        return "Break Start";
      case "center":
        return "Center";
      case "BREAK_END":
        return "Break End";
      default:
        return type;
    }
  };

  const handleDragEnd = (event: L.LeafletEvent, markerId: string): void => {
    const { lat, lng } = (event.target as L.Marker).getLatLng();
    setMarkers((prevMarkers) =>
      prevMarkers.map((marker) =>
        marker.id === markerId ? { ...marker, lat, long: lng } : marker,
      ),
    );
  };

  const deleteMarker = (markerId: string): void => {
    setMarkers((prevMarkers) =>
      prevMarkers.filter((marker) => marker.id !== markerId),
    );
  };

  const createMarkerIcon = (type: string): L.DivIcon => {
    const color = colorMap[type] || "gray";

    return L.divIcon({
      className: "custom-marker-icon",
      html: `<div style="width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="${color}" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>
          <circle cx="12" cy="10" r="3" fill="white"/>
        </svg>
      </div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
  };

  const clockOnMarker = markers.find((marker) => marker.type === "CLOCK_IN");
  const clockOffMarker = markers.find((marker) => marker.type === "CLOCK_OUT");
  const shouldDrawLine = clockOnMarker && clockOffMarker;

  const calculateDistance = (): string | null => {
    if (!clockOnMarker || !clockOffMarker) {
      return null;
    }
    const point1 = L.latLng(clockOnMarker.lat, clockOnMarker.long);
    const point2 = L.latLng(clockOffMarker.lat, clockOffMarker.long);

    return point1.distanceTo(point2).toFixed(2);
  };

  const getCoordinatesString = (position: [number, number]): string => {
    return `${position[0].toFixed(4)}, ${position[1].toFixed(4)}`;
  };

  const canAddMarker = (type: string): boolean => {
    if (!isContinueButtonOn && type === "break") {
      return true;
    }

    if (isContinueButtonOn && type === "continue") {
      return true;
    }

    return !markers.some((marker) => marker.type === type);
  };

  const renderButton = (type: string): JSX.Element => (
    <button
      key={type}
      className='flex items-center gap-2 capitalize p-2 rounded text-white'
      onClick={() => addMarker(type)}
      disabled={!canAddMarker(type)}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='36'
        height='36'
        viewBox='0 0 24 24'
        fill={colorMap[type]}
        stroke={colorMap[type]}
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        className='lucide lucide-map-pin'
      >
        <path d='M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0' />
        <circle cx='12' cy='10' r='3' fill='white' />
      </svg>
      <p style={{ color: colorMap[type] }}>{returnTypeName(type)}</p>
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className='min-w-[60dvw] overflow-y-auto min-h-full'>
        <SheetHeader>
          <SheetTitle>Employee location</SheetTitle>
        </SheetHeader>

        <div className='flex flex-wrap justify-end gap-4 mt-4 mb-2'>
          {Object.keys(colorMap)
            .filter((type) => type !== "center")
            .map((type) => renderButton(type))}
        </div>

        <MapContainer
          center={initialPosition}
          zoom={16}
          scrollWheelZoom
          style={{ height: "500px", width: "100%" }}
        >
          <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />

          <Marker
            position={initialPosition}
            icon={createMarkerIcon("center")}
            draggable={false}
          >
            <Popup>Center Marker</Popup>
          </Marker>

          <Circle
            center={initialPosition}
            radius={projectAddress?.radius || 120}
            pathOptions={{ color: "red", fillOpacity: 0, weight: 2 }}
          >
            <Popup>{projectAddress?.radius || 120} meter radius</Popup>
          </Circle>

          {shouldDrawLine && (
            <Polyline
              positions={[
                [clockOnMarker!.lat, clockOnMarker!.long],
                initialPosition,
              ]}
              pathOptions={{ color: "red", weight: 3, dashArray: "5, 5" }}
            >
              <Popup>Distance: {calculateDistance()} meters</Popup>
            </Polyline>
          )}

          {shouldDrawLine && (
            <Polyline
              positions={[
                [clockOffMarker!.lat, clockOffMarker!.long],
                initialPosition,
              ]}
              pathOptions={{ color: "red", weight: 3, dashArray: "5, 5" }}
            >
              <Popup>Distance: {calculateDistance()} meters</Popup>
            </Polyline>
          )}

          {markers.map((marker) => (
            <Marker
              key={`${marker.id}-${marker.lat}-${marker.long}`}
              position={[marker.lat, marker.long]}
              icon={createMarkerIcon(marker.type)}
              draggable
              eventHandlers={{
                dragend: (event) => handleDragEnd(event, marker.id),
              }}
            >
              <Popup>
                <p>{marker.type} Marker</p>
                <button
                  onClick={() => deleteMarker(marker.id)}
                  className='mt-2 px-3 py-1 bg-red-500 text-white rounded flex items-center'
                >
                  <Trash2 size={16} className='mr-1' /> Delete
                </button>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        <table className='min-w-full divide-y divide-gray-200 border'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Type
              </th>
              <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Coordinates
              </th>
              <th className='px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {/* Center marker row - always present */}
            <tr>
              <td className='px-4 py-2 whitespace-nowrap'>
                <div className='flex items-center'>
                  <div
                    className='w-4 h-4 mr-2 rounded-full'
                    style={{ backgroundColor: colorMap.center }}
                  ></div>
                  Center
                </div>
              </td>
              <td className='px-4 py-2 whitespace-nowrap'>
                {getCoordinatesString(initialPosition)}
              </td>
              <td className='px-4 py-2 whitespace-nowrap text-center'>
                <span
                  className='text-gray-400'
                  title='Cannot delete center marker'
                >
                  <Trash2 size={18} />
                </span>
              </td>
            </tr>
            {markers.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className='px-4 py-2 text-center text-sm text-gray-500'
                >
                  No additional markers available
                </td>
              </tr>
            ) : (
              markers.map((marker) => (
                <tr key={marker.id}>
                  <td className='px-4 py-2 whitespace-nowrap'>
                    <div className='flex items-center'>
                      <div
                        className='w-4 h-4 mr-2 rounded-full'
                        style={{ backgroundColor: colorMap[marker.type] }}
                      ></div>
                      {returnTypeName(marker.type)}
                    </div>
                  </td>
                  <td className='px-4 py-2 whitespace-nowrap'>
                    {getCoordinatesString([marker.lat, marker.long])}
                  </td>
                  <td className='px-4 py-2 whitespace-nowrap text-center'>
                    <button
                      onClick={() => deleteMarker(marker.id)}
                      className='text-red-500 hover:text-red-700'
                      title='Delete marker'
                      disabled
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SheetContent>
    </Sheet>
  );
};

export default LocationMap;
